import React from 'react';
import BoardSlot from './BoardSlot';
import GoalFrame from './GoalFrame';
import { TID } from '@/constants/testIds';
import { validPlacements, validAttackTargets, validDoubleChancePlacements, validDoubleChanceTargets } from '@/engine/rules';

/**
 * Pitch layout (compact, fits a portrait phone without scrolling):
 *
 *   Opponent half (top):
 *     GK row · Defense row · Attack row
 *   ── midline ──
 *   Player half (bottom):
 *     Attack row · Defense row · GK row
 *
 * Click semantics (continuous-turn variant):
 *   - phase === 'drawn'                → standard placement + attack targets
 *   - phase === 'joker-double-action'  → Double-Chance placements (atk/def
 *     slots only — never GK) AND DC attack targets (opp atk/def — never GK).
 */
export default function GameBoard({ state, onPlaceSlot, onAttackTarget, devReveal, size = 'compact', viewer = 'p1' }) {
  const isPlayerTurn = state.currentPlayer === viewer;
  const other = viewer === 'p1' ? 'p2' : 'p1';
  const me = state.players[viewer];
  const opp = state.players[other];

  let placements = [];
  let attackTargets = [];
  if (state.phase === 'drawn' && isPlayerTurn && state.drawnCard) {
    placements = validPlacements(me, state.drawnCard);
    attackTargets = validAttackTargets(state);
  } else if (state.phase === 'joker-double-action' && isPlayerTurn) {
    placements = validDoubleChancePlacements(me);
    attackTargets = validDoubleChanceTargets(state);
  }
  const isValidPlacement = (type, idx) =>
    placements.some((p) => p.slotType === type && p.slotIndex === idx);
  const isAttackTarget = (player, type, idx) =>
    attackTargets.some((t) => t.player === player && t.slotType === type && t.slotIndex === idx);

  const slotSize = size === 'compact' ? 'sm' : 'md';

  return (
    <div className="w-full px-2 py-1 relative">
      {/* Decorative football goals — presentation only, behind cards. */}
      <GoalFrame orientation="top" />
      <GoalFrame orientation="bottom" />
      {/* OPPONENT - top */}
      <div className="flex flex-col items-center gap-0.5 relative" style={{ zIndex: 2 }}>
        <SlotRow label="Goalkeeper" centered>
          <BoardSlot
            card={opp.goalkeeper}
            faceDown={!devReveal && !!opp.goalkeeper}
            gkBack
            isAttackTarget={isAttackTarget(other, 'goalkeeper', 0)}
            onClick={() => isAttackTarget(other, 'goalkeeper', 0) && onAttackTarget?.({ player: other, slotType: 'goalkeeper', slotIndex: 0 })}
            disabled={!isAttackTarget(other, 'goalkeeper', 0)}
            testId={TID.opponentGoalkeeper}
            cardTestId={`${TID.opponentGoalkeeper}-card`}
            label="GK"
            size={slotSize}
          />
        </SlotRow>
        <SlotRow label="Defense">
          {opp.defenses.map((c, i) => (
            <BoardSlot
              key={`opp-def-${i}`}
              card={c}
              isAttackTarget={isAttackTarget(other, 'defense', i)}
              onClick={() => isAttackTarget(other, 'defense', i) && onAttackTarget?.({ player: other, slotType: 'defense', slotIndex: i })}
              disabled={!isAttackTarget(other, 'defense', i)}
              testId={TID.slot(other, 'defense', i)}
              cardTestId={TID.slotCard(other, 'defense', i)}
              label="DEF"
              size={slotSize}
            />
          ))}
        </SlotRow>
        <SlotRow label="Attack">
          {opp.attacks.map((c, i) => (
            <BoardSlot
              key={`opp-atk-${i}`}
              card={c}
              isAttackTarget={isAttackTarget(other, 'attack', i)}
              onClick={() => isAttackTarget(other, 'attack', i) && onAttackTarget?.({ player: other, slotType: 'attack', slotIndex: i })}
              disabled={!isAttackTarget(other, 'attack', i)}
              testId={TID.slot(other, 'attack', i)}
              cardTestId={TID.slotCard(other, 'attack', i)}
              label="ATK"
              size={slotSize}
            />
          ))}
        </SlotRow>
      </div>

      <div className="my-2 midline" />

      {/* PLAYER - bottom */}
      <div className="flex flex-col items-center gap-0.5 relative" style={{ zIndex: 2 }}>
        <SlotRow label="Attack">
          {me.attacks.map((c, i) => (
            <BoardSlot
              key={`me-atk-${i}`}
              card={c}
              isValid={isValidPlacement('attack', i)}
              onClick={() => isValidPlacement('attack', i) && onPlaceSlot?.({ slotType: 'attack', slotIndex: i })}
              disabled={!isValidPlacement('attack', i)}
              testId={TID.slot(viewer, 'attack', i)}
              cardTestId={TID.slotCard(viewer, 'attack', i)}
              label="ATK"
              size={slotSize}
            />
          ))}
        </SlotRow>
        <SlotRow label="Defense">
          {me.defenses.map((c, i) => (
            <BoardSlot
              key={`me-def-${i}`}
              card={c}
              isValid={isValidPlacement('defense', i)}
              onClick={() => isValidPlacement('defense', i) && onPlaceSlot?.({ slotType: 'defense', slotIndex: i })}
              disabled={!isValidPlacement('defense', i)}
              testId={TID.slot(viewer, 'defense', i)}
              cardTestId={TID.slotCard(viewer, 'defense', i)}
              label="DEF"
              size={slotSize}
            />
          ))}
        </SlotRow>
        <SlotRow label="Goalkeeper" centered>
          <BoardSlot
            card={me.goalkeeper}
            isValid={isValidPlacement('goalkeeper', 0)}
            onClick={() => isValidPlacement('goalkeeper', 0) && onPlaceSlot?.({ slotType: 'goalkeeper', slotIndex: 0 })}
            disabled={!isValidPlacement('goalkeeper', 0)}
            testId={TID.playerGoalkeeper}
            cardTestId={`${TID.playerGoalkeeper}-card`}
            label="GK"
            size={slotSize}
          />
        </SlotRow>
      </div>
    </div>
  );
}

function SlotRow({ label, centered, children }) {
  return (
    <div className="flex flex-col items-center" style={{ gap: 1 }}>
      <span className="line-label">{label}</span>
      <div className={`flex gap-1.5 ${centered ? 'justify-center' : ''}`}>
        {children}
      </div>
    </div>
  );
}
